'use client'

import { ResponsiveImage } from '@syconium/little-miss-figgy/dist/components/Image/ResponsiveImage'
import { TypeStyleHeadlineSmall } from '@syconium/little-miss-figgy/dist/components/TypeStyle'

import { gql } from '../../../../../__generated__/graphql/catalog'
import { PageCollectionViewHeroSectionFieldsFragment } from '../../../../../__generated__/graphql/catalog/graphql'
import { trackEvent } from '../../../../../lib/analytics'
import { useTranslation } from '../../../../_providers/TranslationProvider.client'
import { ClientOnlyResponsiveVideo } from '../../../media/ClientOnlyResponsiveVideo.client'
import { WatchVideoDialog } from '../../../media/WatchVideoDialog.client'
import { NextLink } from '../../../navigation/NextLink'

import {
  Container,
  Cta,
  ImageWrap,
  Subtitle,
  SubtitleWrapper,
  TextWrap,
  VideoDialogWrapper,
} from './ContentfulPageCollectionViewHeroSection.styles'

export const pageCollectionViewHeroSectionFields = gql(`
  fragment PageCollectionViewHeroSectionFields on CollectionViewHeroSection {
    __typename
    id
    backgroundColor
    imageDesktop
    imageMobile
    imagePlacement
    inlineVideoDesktop
    inlineVideoMobile
    inlineVimeoIdDesktop
    inlineVimeoIdMobile
    subtitle
    textColor
    title
    videoId
    videoPlatform
    ctaLink
    ctaText
    imageDesktop
    name
  }
`)

type ContentfulPageCollectionViewHeroSectionProps = {
  data: PageCollectionViewHeroSectionFieldsFragment
  additionalContent?: React.ReactNode
  pageSectionIndex: number
}

export const ContentfulPageCollectionViewHeroSection = ({
  pageSectionIndex,
  additionalContent,
  data,
}: ContentfulPageCollectionViewHeroSectionProps) => {
  const translations = useTranslation()
  const section = data

  const {
    backgroundColor,
    textColor,
    title,
    subtitle,
    ctaLink,
    ctaText,
    name,
    imagePlacement,
    inlineVideoDesktop,
    inlineVideoMobile,
    inlineVimeoIdDesktop,
    inlineVimeoIdMobile,
  } = section

  const imageAsset =
    section.imageDesktop && section.imageMobile
      ? {
          srcFromMd: section.imageDesktop,
          srcUntilMd: section.imageMobile,
          isCritical: pageSectionIndex === 0,
        }
      : undefined

  const modalVideoAsset =
    section.videoId && section.videoPlatform
      ? {
          id: section.videoId,
          platform: section.videoPlatform,
        }
      : undefined

  return (
    <Container
      data-testid='hero-section'
      $backgroundColor={backgroundColor}
      $desktopTextAlign={imageAsset ? 'left' : 'center'}
      $textColor={textColor}
      hasVerticalMargin={false}
    >
      <TextWrap $desktopWidth={imageAsset ? '50%' : '100%'}>
        {title && <TypeStyleHeadlineSmall as='h2'>{title}</TypeStyleHeadlineSmall>}
        {subtitle && (
          <SubtitleWrapper>
            <Subtitle>{subtitle}</Subtitle>
          </SubtitleWrapper>
        )}

        {additionalContent ? <div>{additionalContent}</div> : null}

        {ctaLink && ctaText ? (
          <Cta
            forwardedAs={NextLink}
            $variant={'white-on-black-no-border'}
            {...trackEvent({
              category: 'CollectionViewHeroSection',
              action: 'click',
              label: ctaText,
              pageSectionIndex,
              pageSectionName: title ?? undefined,
              correspondingAsset: imageAsset?.srcFromMd,
              contentfulName: name ?? undefined,
            })}
            href={ctaLink}
          >
            {ctaText}
          </Cta>
        ) : null}
      </TextWrap>

      {imageAsset ? (
        <ImageWrap $imagePlacement={imagePlacement}>
          <ResponsiveImage
            src={imageAsset.srcUntilMd}
            alt={title ?? ''}
            aspectRatios={{
              sm: null,
              md: null,
            }}
            loading={imageAsset.isCritical ? 'default' : 'lazy'}
            fetchPriority={imageAsset.isCritical ? 'high' : undefined}
            widths={{ unit: 'vw', sm: 100, md: 50 }}
            srcs={{
              sm: imageAsset.srcUntilMd,
              md: imageAsset.srcFromMd,
            }}
          />
          <ClientOnlyResponsiveVideo
            playText={translations.magnolia.general.playVideo}
            pauseText={translations.magnolia.general.pauseVideo}
            inlineVideoDesktop={inlineVideoDesktop}
            inlineVideoMobile={inlineVideoMobile}
            inlineVimeoIdDesktop={inlineVimeoIdDesktop}
            inlineVimeoIdMobile={inlineVimeoIdMobile}
          />
          {modalVideoAsset ? (
            <VideoDialogWrapper>
              <WatchVideoDialog
                videoId={modalVideoAsset.id}
                videoPlatform={modalVideoAsset.platform === 'youtube' ? 'youtube' : 'vimeo'}
              />
            </VideoDialogWrapper>
          ) : null}
        </ImageWrap>
      ) : null}
    </Container>
  )
}
